// 独立站 AI 流水线 demo — 5 个共享组件

import React from 'react';
import { Steps, Tag, Card, Tooltip, Progress, Timeline, Avatar, Image, Badge, Row, Col, Statistic, Divider, Empty } from 'antd';
import {
  CheckCircleOutlined, CloseCircleOutlined, ClockCircleOutlined,
  EditOutlined, SelectOutlined, RocketOutlined, ExperimentOutlined,
  EyeOutlined, FilterOutlined
} from '@ant-design/icons';

import {
  LifecycleStatus, AiCandidateQuality, AiPriceRecommendation, AiCandidateDecision
} from './types';
import { STATUS_LABELS, STATUS_COLORS, REJECT_REASON_LABELS } from './data';

// ============================================================
// 1. LifecycleStateBar — 10 状态机进度条
// ============================================================

const PROGRESS_ORDER: LifecycleStatus[] = [
  'DISCOVERED', 'SELECTED', 'IMAGE_GENERATING', 'IMAGE_REVIEW',
  'LISTING_DRAFT', 'PRICED', 'SHOPIFY_DRAFT', 'PUBLISHED', 'MEASURING', 'LEARNED'
];

export const LifecycleStateBar: React.FC<{ status: LifecycleStatus }> = ({ status }) => {
  if (status === 'REJECTED' || status === 'ARCHIVED') {
    return (
      <div style={{ padding: '16px 24px', background: '#fff1f0', borderRadius: 8, border: '1px solid #ffccc7' }}>
        <span style={{ color: '#cf1322', fontWeight: 600 }}>
          <CloseCircleOutlined /> {STATUS_LABELS[status]}
        </span>
        <span style={{ marginLeft: 12, color: '#8c8c8c', fontSize: 12 }}>
          流程终止，查看下方决策审计了解原因
        </span>
      </div>
    );
  }
  const currentIdx = PROGRESS_ORDER.indexOf(status);
  return (
    <Steps
      size="small"
      current={currentIdx}
      style={{ padding: '8px 0' }}
      labelPlacement="vertical"
    >
      {PROGRESS_ORDER.map((s, i) => (
        <Steps.Step
          key={s}
          title={STATUS_LABELS[s]}
          description={i < currentIdx ? '✓' : i === currentIdx ? '当前' : ''}
          status={(i < currentIdx ? 'finish' : i === currentIdx ? 'process' : 'wait') as any}
        />
      ))}
    </Steps>
  );
};

// ============================================================
// 2. EndToEndFlowChart — 9 步端到端链路
// ============================================================

const FLOW_STEPS = [
  { title: '趋势/风格雷达', desc: 'Pinterest + 站内搜索增长率 + 询单 topic NLP', icon: <ExperimentOutlined /> },
  { title: 'AI 候选表记录', desc: 'insert ai_candidate (DISCOVERED) + ref', icon: <EditOutlined /> },
  { title: '图片生成', desc: '调 saas-image-service → ai_toolbox_gen_batch', icon: <RocketOutlined /> },
  { title: '运营审图', desc: 'CV 粗筛 + 运营 3 选 1 + 拒图原因落库', icon: <FilterOutlined /> },
  { title: 'AI listing + 三档价', desc: 'DeepSeek 写文案 + 相似款召回定价', icon: <EditOutlined /> },
  { title: 'Shopify DRAFT', desc: 'EdataShopifyProductService.batchSaleProduct', icon: <SelectOutlined /> },
  { title: '人工 publish', desc: '一键 publishablePublish 转 ACTIVE', icon: <CheckCircleOutlined /> },
  { title: '站内事件回灌', desc: 'idp_event 按 candidate_id 打标 → effect_daily', icon: <EyeOutlined /> },
  { title: '7/14/30 复盘', desc: 'outcome HIT/MISS/PARTIAL → 更新 rubric', icon: <ClockCircleOutlined /> },
];

export const EndToEndFlowChart: React.FC<{ status: LifecycleStatus }> = ({ status }) => {
  const currentIdx = PROGRESS_ORDER.indexOf(status);
  const stepIdx = currentIdx <= 1 ? currentIdx : currentIdx === 2 ? 2 : currentIdx === 3 ? 3 : currentIdx <= 5 ? 4 : currentIdx === 6 ? 5 : currentIdx === 7 ? 6 : currentIdx === 8 ? 7 : 8;
  return (
    <div style={{ padding: 16, background: '#fafafa', borderRadius: 8 }}>
      <Row gutter={[12, 12]}>
        {FLOW_STEPS.map((step, i) => {
          const isPast = i < stepIdx;
          const isCurrent = i === stepIdx;
          return (
            <Col span={8} key={i}>
              <Card
                size="small"
                style={{
                  borderColor: isCurrent ? '#1890ff' : isPast ? '#52c41a' : '#d9d9d9',
                  background: isCurrent ? '#e6f7ff' : isPast ? '#f6ffed' : '#fff',
                  borderWidth: isCurrent ? 2 : 1,
                }}
              >
                <div style={{ fontSize: 12, color: '#8c8c8c' }}>
                  Step {i + 1} {isPast && <Tag color="success" style={{ marginLeft: 4 }}>已完成</Tag>}
                  {isCurrent && <Tag color="processing" style={{ marginLeft: 4 }}>当前</Tag>}
                </div>
                <div style={{ fontWeight: 600, margin: '6px 0', fontSize: 13 }}>
                  {step.icon} {step.title}
                </div>
                <div style={{ fontSize: 11, color: '#595959' }}>{step.desc}</div>
              </Card>
            </Col>
          );
        })}
      </Row>
    </div>
  );
};

// ============================================================
// 3. ImageCandidateGrid — 图候选并排（CV 粗筛 + 一键选/拒）
// ============================================================

export const ImageCandidateGrid: React.FC<{
  qualities: AiCandidateQuality[];
  onSelect?: (id: string) => void;
  onReject?: (id: string) => void;
}> = ({ qualities, onSelect, onReject }) => {
  if (qualities.length === 0) return <Empty description="无图候选" />;

  const visible = qualities.filter(q => !q.cv_filtered);
  const filtered = qualities.filter(q => q.cv_filtered);

  return (
    <div>
      <div style={{ marginBottom: 12 }}>
        <Tag color="green">已通过 CV 粗筛 {visible.length} 张</Tag>
        <Tag color="red">CV 剔除 {filtered.length} 张</Tag>
        <span style={{ marginLeft: 8, color: '#8c8c8c', fontSize: 12 }}>
          运营只需在 {visible.length} 张里挑（节省看 {filtered.length} 张废图的时间）
        </span>
      </div>
      <Row gutter={[12, 12]}>
        {visible.map(q => (
          <Col span={6} key={q.id}>
            <Card
              size="small"
              hoverable
              style={{ borderColor: q.is_selected ? '#52c41a' : undefined, borderWidth: q.is_selected ? 2 : 1 }}
              cover={
                <div style={{ position: 'relative', height: 180, overflow: 'hidden' }}>
                  <Image src={q.image_url} alt="" preview={false}
                    style={{ width: '100%', height: 180, objectFit: 'cover' }} />
                  {q.is_selected && (
                    <Badge.Ribbon text="已选" color="green" style={{ top: 8 }} />
                  )}
                </div>
              }
              actions={[
                <span onClick={() => onSelect?.(q.id)} key="select"><CheckCircleOutlined style={{ color: '#52c41a' }} /> 选</span>,
                <span onClick={() => onReject?.(q.id)} key="reject"><CloseCircleOutlined style={{ color: '#cf1322' }} /> 拒</span>,
              ]}
            >
              <div style={{ fontSize: 12 }}>
                <div>质量分: <Tag color={q.quality_score > 80 ? 'green' : q.quality_score > 60 ? 'orange' : 'red'}>{q.quality_score}</Tag></div>
                <div style={{ marginTop: 4 }}>
                  视觉一致: {q.visual_consistency_score} · 石位: {q.stone_position_accuracy} · 背景: {q.background_fit_score}
                </div>
                {q.reject_tags && q.reject_tags.length > 0 && (
                  <div style={{ marginTop: 4 }}>
                    {q.reject_tags.map(t => <Tag key={t} color="red">{REJECT_REASON_LABELS[t]}</Tag>)}
                  </div>
                )}
              </div>
            </Card>
          </Col>
        ))}
      </Row>
      {filtered.length > 0 && (
        <>
          <Divider plain orientation="left" style={{ marginTop: 24 }}>
            <span style={{ color: '#8c8c8c', fontSize: 12 }}>CV 已剔废图（运营默认不看）</span>
          </Divider>
          <Row gutter={[8, 8]}>
            {filtered.map(q => (
              <Col span={4} key={q.id}>
                <div style={{ position: 'relative', opacity: 0.4 }}>
                  <Image src={q.image_url} preview={false} style={{ width: '100%', height: 80, objectFit: 'cover', filter: 'grayscale(60%)' }} />
                  <div style={{ position: 'absolute', bottom: 4, left: 4, fontSize: 10, background: '#000', color: '#fff', padding: '1px 4px' }}>
                    {q.cv_filter_reason}
                  </div>
                </div>
              </Col>
            ))}
          </Row>
        </>
      )}
    </div>
  );
};

// ============================================================
// 4. PriceTierCard — 三档价 + 相似款依据 + 置信度
// ============================================================

export const PriceTierCard: React.FC<{ rec: AiPriceRecommendation }> = ({ rec }) => {
  const fmtUSD = (cents: number) => '$' + (cents / 100).toFixed(2);

  return (
    <Card title={
      <span>三档价推荐 <Tag color={rec.confidence > 0.8 ? 'green' : rec.confidence > 0.7 ? 'orange' : 'red'}>置信度 {(rec.confidence * 100).toFixed(0)}%</Tag></span>
    }>
      <Row gutter={16}>
        <Col span={8}>
          <Card size="small" style={{ background: '#fafafa' }}>
            <Statistic title="保守价" value={fmtUSD(rec.conservative_price_cents)} valueStyle={{ color: '#1890ff', fontSize: 24 }} />
            <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: 4 }}>稳赚不亏</div>
          </Card>
        </Col>
        <Col span={8}>
          <Card size="small" style={{ background: '#e6f7ff', borderColor: '#1890ff', borderWidth: 2 }}>
            <Statistic title="建议价 ★" value={fmtUSD(rec.recommended_price_cents)} valueStyle={{ color: '#cf1322', fontSize: 28, fontWeight: 700 }} />
            <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: 4 }}>AI 综合推荐</div>
          </Card>
        </Col>
        <Col span={8}>
          <Card size="small" style={{ background: '#fafafa' }}>
            <Statistic title="测试价" value={fmtUSD(rec.test_price_cents)} valueStyle={{ color: '#fa8c16', fontSize: 24 }} />
            <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: 4 }}>挑战高毛利</div>
          </Card>
        </Col>
      </Row>

      <Divider plain>价格依据</Divider>

      <Row>
        <Col span={12}>
          <h4 style={{ marginBottom: 8 }}>成本假设</h4>
          <div style={{ fontSize: 12, lineHeight: 1.8 }}>
            • 克重: {rec.cost_assumptions.pure_gold_weight_g} g<br />
            • 当前金价: ¥{rec.gold_price_at_calc}/g · 汇率: {rec.exchange_rate_at_calc}<br />
            • 工费: {fmtUSD(rec.cost_assumptions.hand_fee_cents)}<br />
            • 主石: {fmtUSD(rec.cost_assumptions.main_stone_value_cents)} · 副石: {fmtUSD(rec.cost_assumptions.slave_stone_value_cents)}<br />
            • 倍率: {rec.multiple_used}
          </div>
        </Col>
        <Col span={12}>
          <h4 style={{ marginBottom: 8 }}>相似款 K 近邻（{rec.similar_items.length}）</h4>
          {rec.similar_items.map((s, i) => (
            <div key={i} style={{ fontSize: 12, padding: '4px 0', borderBottom: '1px dashed #f0f0f0' }}>
              <Tag color="blue">相似度 {(s.similarity * 100).toFixed(0)}%</Tag>
              价 {fmtUSD(s.price_cents)} · 倍率 {s.multiple}
              <div style={{ color: '#8c8c8c', fontSize: 11 }}>{s.ref_id}</div>
            </div>
          ))}
        </Col>
      </Row>
    </Card>
  );
};

// ============================================================
// 5. DecisionAuditTimeline — 决策审计时间线
// ============================================================

const DECISION_LABELS: Record<string, string> = {
  SELECT: '选定 / 推进',
  REJECT: '拒绝',
  REGENERATE_IMAGE: '重抽图',
  EDIT_LISTING: '编辑文案',
  EDIT_PRICE: '改价',
  PUBLISH: '发布',
  ARCHIVE: '归档',
};

const DECISION_COLORS: Record<string, string> = {
  SELECT: 'blue',
  REJECT: 'red',
  REGENERATE_IMAGE: 'orange',
  EDIT_LISTING: 'purple',
  EDIT_PRICE: 'magenta',
  PUBLISH: 'green',
  ARCHIVE: 'gray',
};

export const DecisionAuditTimeline: React.FC<{ decisions: AiCandidateDecision[] }> = ({ decisions }) => {
  if (decisions.length === 0) return <Empty description="无审计记录" />;

  return (
    <Timeline mode="left">
      {decisions.map(d => (
        <Timeline.Item
          key={d.id}
          color={DECISION_COLORS[d.decision_type] || 'blue'}
          label={<span style={{ fontSize: 11 }}>{new Date(d.decided_at).toLocaleString('zh-CN')}</span>}
        >
          <div>
            <Tag color={DECISION_COLORS[d.decision_type]}>{DECISION_LABELS[d.decision_type]}</Tag>
            <Avatar size="small" style={{ marginLeft: 8, marginRight: 4 }}>{d.operator_name[0]}</Avatar>
            <span style={{ fontSize: 12 }}>{d.operator_name}</span>
          </div>
          <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: 4 }}>
            {STATUS_LABELS[d.before_status]} → {STATUS_LABELS[d.after_status]}
          </div>
          {d.reason_code && (
            <div style={{ fontSize: 12, marginTop: 4 }}>
              原因码: <Tag color="red">{REJECT_REASON_LABELS[d.reason_code as any] || d.reason_code}</Tag>
            </div>
          )}
          {d.reason_text && (
            <div style={{ fontSize: 12, marginTop: 4, color: '#262626' }}>"{d.reason_text}"</div>
          )}
        </Timeline.Item>
      ))}
    </Timeline>
  );
};
